import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, HStack, VStack, Text, SimpleGrid, Skeleton, SkeletonCircle } from "@chakra-ui/react";
import { HiOutlineUserGroup, HiOutlineChevronLeft } from "react-icons/hi2";
import useSetPageHeader from "@/components/hooks/useSetPageHeader";
import { useAuth } from "@/auth/AuthContext";
import { useAgentTypes } from "@/components/layout/AgentTypesContext";
import { ModelService } from "@/services/ModelService";

const UNASSIGNED_KEY = "__unassigned__";

const AVATAR_PALETTE = [
    { bg: "#EDE9FE", color: "#6D28D9" },
    { bg: "#DCFCE7", color: "#15803D" },
    { bg: "#FEF3C7", color: "#B45309" },
    { bg: "#DBEAFE", color: "#1D4ED8" },
    { bg: "#FCE7F3", color: "#BE185D" },
];

const initialsOf = (name) =>
    (name ?? "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const RecruitmentTile = ({ recruitment, onClick }) => (
    <Box
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius={10}
        p={3}
        cursor="pointer"
        transition="border-color 0.15s, box-shadow 0.15s"
        _hover={{ borderColor: "purple.200", boxShadow: "0 4px 14px rgba(23,21,31,0.06)" }}
        onClick={onClick}
    >
        <Text color="gray.900" fontSize="sm" fontWeight={600} truncate>{recruitment.name}</Text>
        <Text color="gray.400" fontSize="xs" truncate mt={0.5}>
            {[recruitment.companyName, recruitment.location].filter(Boolean).join(" · ") || "—"}
        </Text>
    </Box>
);

const ScenarioSection = ({ scenarioGroup, onTileClick }) => (
    <Box w="full">
        <Text
            display="inline-block"
            color="purple.700"
            bg="purple.50"
            fontSize="11px"
            fontWeight={600}
            textTransform="uppercase"
            letterSpacing="wider"
            px={2.5}
            py={1}
            borderRadius={6}
            mb={2}
        >
            {scenarioGroup.scenarioName}
        </Text>
        <SimpleGrid columns={{ base: 1, sm: 2 }} gap={3}>
            {scenarioGroup.recruitments.map((recruitment) => (
                <RecruitmentTile key={recruitment.id} recruitment={recruitment} onClick={() => onTileClick(recruitment)} />
            ))}
        </SimpleGrid>
    </Box>
);

const AllRecruitmentsPage = () => {
    const { t } = useTranslation("home");
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { recruiterAgents, isLoading: agentsLoading } = useAgentTypes();

    useSetPageHeader(t("allRecruitments.headerTitle"), t("allRecruitments.headerSubtitle"), <HiOutlineUserGroup />);

    const [isLoadingData, setIsLoadingData] = useState(true);
    const [activeRecruitments, setActiveRecruitments] = useState([]);
    const [scenarioAgentMap, setScenarioAgentMap] = useState({});

    useEffect(() => {
        if (agentsLoading || !currentUser?.id) return;

        let cancelled = false;
        setIsLoadingData(true);

        const agentDetailsPromise = Promise.allSettled(
            recruiterAgents.map((agent) => ModelService.getById(agent.id).then((details) => ({ agent, details })))
        );
        const activeRecruitmentsPromise = ModelService.getActiveWorkflowsByOwner(currentUser.id, 1, 200);

        Promise.all([agentDetailsPromise, activeRecruitmentsPromise]).then(([agentDetailsResults, workflowsResponse]) => {
            if (cancelled) return;

            const map = {};
            agentDetailsResults.forEach((result) => {
                if (result.status !== "fulfilled") return;
                const { agent, details } = result.value;
                (details?.scenarios ?? []).forEach((scenario) => {
                    map[scenario.id] = { agentId: agent.id, agentName: agent.name, scenarioName: scenario.name };
                });
            });

            setScenarioAgentMap(map);
            setActiveRecruitments(workflowsResponse?.items ?? []);
            setIsLoadingData(false);
        }).catch(() => {
            if (!cancelled) setIsLoadingData(false);
        });

        return () => {
            cancelled = true;
        };
    }, [agentsLoading, currentUser?.id, recruiterAgents]);

    const groupedByAgent = useMemo(() => {
        const agentGroups = new Map();

        const getAgentGroup = (agentId, agentName) => {
            if (!agentGroups.has(agentId)) {
                agentGroups.set(agentId, { agentId, agentName, scenarioGroups: new Map() });
            }
            return agentGroups.get(agentId);
        };

        activeRecruitments.forEach((recruitment) => {
            const resolved = scenarioAgentMap[recruitment.scenario?.id];
            const agentId = resolved?.agentId ?? UNASSIGNED_KEY;
            const agentName = resolved?.agentName ?? t("allRecruitments.unassignedAgent");
            const scenarioId = recruitment.scenario?.id ?? UNASSIGNED_KEY;
            const scenarioName = resolved?.scenarioName ?? recruitment.scenario?.name ?? t("allRecruitments.unassignedScenario");

            const agentGroup = getAgentGroup(agentId, agentName);
            if (!agentGroup.scenarioGroups.has(scenarioId)) {
                agentGroup.scenarioGroups.set(scenarioId, { scenarioId, scenarioName, recruitments: [] });
            }
            agentGroup.scenarioGroups.get(scenarioId).recruitments.push(recruitment);
        });

        return Array.from(agentGroups.values()).map((group) => ({
            ...group,
            scenarioGroups: Array.from(group.scenarioGroups.values()),
        }));
    }, [activeRecruitments, scenarioAgentMap, t]);

    const resolveAgentId = (recruitment) => {
        const resolved = scenarioAgentMap[recruitment.scenario?.id];
        return resolved?.agentId ?? recruiterAgents[0]?.id;
    };

    if (agentsLoading || isLoadingData) {
        return (
            <VStack w="full" py={6} gap={4} align="flex-start">
                <Skeleton height="14px" width="140px" borderRadius={4} />
                <SimpleGrid columns={{ base: 1, lg: 2, xl: 3 }} gap={4} w="full" alignItems="start">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <Box key={i} w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={4}>
                            <HStack gap={3} mb={4}>
                                <SkeletonCircle size="36px" />
                                <Skeleton height="16px" width="140px" borderRadius={4} />
                            </HStack>
                            <Skeleton height="18px" width="120px" borderRadius={6} mb={2} />
                            <SimpleGrid columns={{ base: 1, sm: 2 }} gap={3}>
                                {Array.from({ length: 2 }).map((__, j) => (
                                    <Box key={j} border="1px solid" borderColor="gray.200" borderRadius={10} p={3}>
                                        <Skeleton height="14px" width="70%" borderRadius={4} mb={2} />
                                        <Skeleton height="10px" width="50%" borderRadius={4} />
                                    </Box>
                                ))}
                            </SimpleGrid>
                        </Box>
                    ))}
                </SimpleGrid>
            </VStack>
        );
    }

    return (
        <VStack w="full" py={6} gap={4} align="flex-start">
            <HStack
                as="button"
                onClick={() => navigate("/recruitment")}
                color="gray.500"
                fontSize="sm"
                gap={1}
                cursor="pointer"
                _hover={{ color: "gray.700" }}
            >
                <HiOutlineChevronLeft size={14} /> {t("allRecruitments.backToDashboard")}
            </HStack>

            {activeRecruitments.length === 0 ? (
                <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={6}>
                    <Text color="gray.500" fontSize="sm">{t("allRecruitments.empty")}</Text>
                </Box>
            ) : (
                <SimpleGrid columns={{ base: 1, lg: 2, xl: 3 }} gap={4} w="full" alignItems="start">
                    {groupedByAgent.map((agentGroup, index) => {
                        const palette = AVATAR_PALETTE[index % AVATAR_PALETTE.length];
                        return (
                            <Box key={agentGroup.agentId} w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={4}>
                                <HStack gap={3} mb={4}>
                                    <Box
                                        w="36px" h="36px" borderRadius={10} bg={palette.bg} color={palette.color}
                                        display="flex" alignItems="center" justifyContent="center" fontWeight={700} fontSize="sm" flexShrink={0}
                                    >
                                        {initialsOf(agentGroup.agentName)}
                                    </Box>
                                    <Text fontWeight={700} color="gray.900" fontSize="md">{agentGroup.agentName}</Text>
                                </HStack>
                                <VStack w="full" gap={4} align="stretch">
                                    {agentGroup.scenarioGroups.map((scenarioGroup) => (
                                        <ScenarioSection
                                            key={scenarioGroup.scenarioId}
                                            scenarioGroup={scenarioGroup}
                                            onTileClick={(recruitment) => navigate(`/models/${resolveAgentId(recruitment)}/recruitments/${recruitment.id}/candidates`)}
                                        />
                                    ))}
                                </VStack>
                            </Box>
                        );
                    })}
                </SimpleGrid>
            )}
        </VStack>
    );
};

export default AllRecruitmentsPage;
